import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customers, type Customer } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { Plus, Search, Edit3, Trash2 } from 'lucide-react';

export default function Customers() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  // Reset page quand la recherche change
  useEffect(() => { setPage(1); }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ['customers', q, page],
    queryFn: () => customers.list({ q: q || undefined, per_page: 50, page }),
  });

  const destroyMut = useMutation({
    mutationFn: (id: number) =>
      toast.promise(customers.destroy(id), {
        loading: 'Suppression…',
        success: 'Customer supprimé',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
  });

  function openCreate() { setEditing(null); setModalOpen(true); }
  function openEdit(c: Customer) { setEditing(c); setModalOpen(true); }

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle={`${data?.total ?? 0} comptes`}
        actions={
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            <Plus size={11} />Nouveau customer
          </button>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="p-4 border-b border-border bg-bg-2">
          <div className="relative max-w-md">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-faint" />
            <input
              className="input pl-7 w-full"
              placeholder="Rechercher nom, email, téléphone, external_id…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        <table className="luc-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>External ID</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Adresse</th>
              <th>Status</th>
              <th>Plan</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {data?.data.map((c) => (
              <tr key={c.id} className="cursor-pointer hover:bg-bg-2">
                <td><Link to={`/app/customers/${c.id}`} className="font-medium hover:text-brand">{c.name}</Link></td>
                <td className="font-mono text-[12px] text-text-muted">{c.external_id ?? '—'}</td>
                <td className="text-text-muted">{c.email ?? '—'}</td>
                <td className="font-mono text-[12px] text-text-muted">{c.phone ?? '—'}</td>
                <td className="text-text-muted text-xs">{formatAddress(c)}</td>
                <td>
                  <span className={`badge ${c.status === 'active' ? 'badge-up' : 'badge-warn'}`}>{c.status}</span>
                </td>
                <td className="text-text-muted">{c.service_plan_id ? `#${c.service_plan_id}` : '—'}</td>
                <td>
                  <div className="flex items-center gap-1 justify-end">
                    <button className="btn btn-ghost btn-sm" title="Modifier" onClick={() => openEdit(c)}>
                      <Edit3 size={11} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm text-danger"
                      title="Supprimer"
                      onClick={() => { if (window.confirm(`Supprimer ${c.name} ?`)) destroyMut.mutate(c.id); }}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!isLoading && !data?.data?.length && (
              <tr><td colSpan={8} className="text-center py-12 text-text-faint">Aucun customer.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.last_page > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-bg-2 shrink-0">
          <div className="text-xs text-text-faint">
            {((page - 1) * data.per_page) + 1}-{Math.min(page * data.per_page, data.total)} / {data.total}
          </div>
          <div className="flex gap-1.5">
            <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</button>
            <span className="text-xs text-text-muted self-center">Page {page} / {data.last_page}</span>
            <button className="btn btn-sm" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        </div>
      )}

      <CustomerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
      />
    </>
  );
}

function formatAddress(c: Customer): string {
  const parts = [c.address_line1, c.city, c.state, c.postal_code].filter(Boolean);
  return parts.length === 0 ? '—' : parts.join(', ');
}

type Form = {
  name: string;
  external_id: string;
  email: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  status: string;
};

const EMPTY: Form = {
  name: '', external_id: '', email: '', phone: '',
  address_line1: '', address_line2: '', city: '', state: '',
  postal_code: '', country: 'CA', status: 'active',
};

function CustomerModal({ open, onClose, editing }: { open: boolean; onClose: () => void; editing: Customer | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<Form>(EMPTY);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        name: editing.name ?? '',
        external_id: editing.external_id ?? '',
        email: editing.email ?? '',
        phone: editing.phone ?? '',
        address_line1: editing.address_line1 ?? '',
        address_line2: editing.address_line2 ?? '',
        city: editing.city ?? '',
        state: editing.state ?? '',
        postal_code: editing.postal_code ?? '',
        country: editing.country ?? 'CA',
        status: editing.status ?? 'active',
      } : EMPTY);
    }
  }, [open, editing]);

  const save = useMutation({
    mutationFn: () => {
      // Strip empty strings — backend attend null pour optionnels
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      );
      return toast.promise(
        editing ? customers.update(editing.id, payload) : customers.create(payload),
        {
          loading: editing ? 'Mise à jour…' : 'Création…',
          success: editing ? 'Customer mis à jour' : 'Customer créé',
        }
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={editing ? `Modifier ${editing.name}` : 'Nouveau customer'} width="max-w-2xl">
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom" required>
            <input className="input w-full" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Field>
          <Field label="External ID (Sonar, CMS…)">
            <input className="input w-full" value={form.external_id}
              onChange={(e) => setForm({ ...form, external_id: e.target.value })} placeholder="SONAR-1234" />
          </Field>
          <Field label="Email">
            <input type="email" className="input w-full" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Téléphone">
            <input className="input w-full" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="514-555-0100" />
          </Field>
        </div>

        <div className="pt-1 border-t border-border" />
        <div className="text-[10.5px] uppercase tracking-wider text-text-faint">Adresse</div>

        <Field label="Adresse (numéro civique + rue)">
          <input className="input w-full" value={form.address_line1}
            onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
            placeholder="123 rue Sainte-Catherine Ouest" />
        </Field>
        <Field label="Adresse (suite, app., bureau)">
          <input className="input w-full" value={form.address_line2}
            onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
            placeholder="App. 402" />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Ville">
            <input className="input w-full" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Montréal" />
          </Field>
          <Field label="Province/État">
            <input className="input w-full" value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="QC" />
          </Field>
          <Field label="Code postal">
            <input className="input w-full" value={form.postal_code}
              onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="H3B 1A1" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pays (code ISO 2 lettres)">
            <input className="input w-full" value={form.country} maxLength={2}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Statut">
            <select className="input w-full" value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
              <option value="churned">Résilié</option>
            </select>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button type="button" onClick={onClose} className="btn">Annuler</button>
          <button type="submit" disabled={save.isPending} className="btn btn-primary">
            {save.isPending ? (editing ? 'Mise à jour…' : 'Création…') : (editing ? 'Mettre à jour' : 'Créer')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-muted block mb-1">{label}{required && <span className="text-danger ml-0.5">*</span>}</span>
      {children}
    </label>
  );
}
